import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import cuid from "cuid";
import { useCallback, useContext, useEffect, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import TemplateIcon from "@/components/Icons/TemplateIcon";
import TextMessageInput from "@/components/TextMessageInput";
import { ampli } from "@/config";
import { type FleetRecipient, summarizeRecipients } from "@/hooks/dispatch/fleetRecipients";
import TusHooks from "@/hooks/UseTusHooks/TusHooks";
import { ActionContext } from "@/models/ActionsProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { recordSpan } from "@/services/telemetry";
import { zinc } from "@/ui/tokens";
import TemplateChoices, { toolPopoverPaperSx } from "./TemplateChoices";

const MESSAGE_LIMIT = 5000;

/** What the composer is doing: taking the message, sending it, or showing that it went. */
type Stage = "compose" | "sending" | "sent";

const buttonSx = {
  height: 34,
  px: 1.75,
  borderRadius: 1,
  fontSize: "0.875rem",
  fontWeight: 600,
} as const;

/**
 * The Dispatch page's fleet message composer: one message to the drivers the page shows, or to the ones ticked, sent
 * right now. Who gets it is the dashboard's call (its filters and checkboxes), so the composer only says who that is,
 * naming a few and counting many, and takes the message, typed, from a template, or a file, with the option to deliver
 * it silently. When the page's filters chose the recipients, the composer repeats those filters as read-only chips, so
 * the dispatcher sees what "everyone shown" means before sending. The plumbing underneath is the same broadcast the
 * rest of the app sends; only the experience is this page's.
 */
export default function DispatchFleetMessage({
  open,
  recipients,
  scope,
  filters,
  onClose,
  onSent,
}: {
  open: boolean;
  recipients: readonly FleetRecipient[];
  /** Where the recipients came from, for the summary line. */
  scope: "ticked" | "shown";
  /** The narrowing behind "shown", as the query strip words it, so the composer says why these drivers. */
  filters: readonly string[];
  onClose: () => void;
  /** The message went; the page drops the ticked rows it was addressed to. */
  onSent: () => void;
}) {
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const { publishBroadcast } = useContext(ActionContext);
  const [stage, setStage] = useState<Stage>("compose");
  const [text, setText] = useState("");
  const [textValid, setTextValid] = useState(false);
  const [silent, setSilent] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [templatesAnchor, setTemplatesAnchor] = useState<HTMLElement | null>(null);
  const [uploadContentId, setUploadContentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<readonly FleetRecipient[]>([]);

  // Each opening starts a fresh message; nothing from the last one lingers.
  useEffect(() => {
    if (!open) return;
    setStage("compose");
    setText("");
    setTextValid(false);
    setSilent(false);
    setAttaching(false);
    setTemplatesAnchor(null);
    setUploadContentId(null);
    setError(null);
  }, [open]);

  const canSend = stage === "compose" && recipients.length > 0 && (attaching ? uploadContentId != null : textValid);

  const send = useCallback(async () => {
    if (!workspaceId || !canSend) return;
    setStage("sending");
    setError(null);
    const startedAt = new Date();
    const attributes = {
      fleetMessage: { recipients: recipients.length, source: "dispatch", hasAttachment: attaching, isSilent: silent },
    };
    try {
      await publishBroadcast?.({
        workspaceId,
        contentId: attaching && uploadContentId ? uploadContentId : cuid(),
        feedIds: recipients.map((recipient) => recipient.feedId),
        ...(attaching ? {} : { inputText: text.trim() }),
        isSilent: silent,
      });
      recordSpan("fleet_message.send", startedAt, attributes);
      setSentTo(recipients);
      setStage("sent");
      onSent();
    } catch (caught) {
      recordSpan("fleet_message.send_failed", startedAt, attributes, "reliability", { isError: true });
      const status = (caught as { status?: number } | null)?.status;
      setError(
        status === 403
          ? "The message did not send. Refresh the page and try again."
          : (caught as Error | null)?.message || "The message did not send.",
      );
      setStage("compose");
    }
  }, [workspaceId, canSend, recipients, attaching, uploadContentId, text, silent, publishBroadcast, onSent]);

  const summary = summarizeRecipients(recipients);
  const scopeWord = scope === "ticked" ? "ticked" : "shown";

  return (
    <Dialog
      open={open}
      onClose={stage === "sending" ? undefined : onClose}
      slotProps={{
        paper: {
          "data-sb": TestIds.dispatch.fleetDialog,
          sx: { width: 560, maxWidth: "94vw", bgcolor: zinc[900], color: "white", border: `1px solid ${zinc[700]}` },
        } as React.ComponentProps<typeof Dialog>["PaperProps"],
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", px: 2.5, pt: 2, pb: 1 }}>
        <Typography component="h2" sx={{ fontSize: "1.0625rem", fontWeight: 700, flex: 1 }}>
          Fleet message
        </Typography>
        <IconButton
          size="small"
          aria-label="Close"
          onClick={onClose}
          disabled={stage === "sending"}
          sx={{ color: zinc[400], "&:hover": { color: "white" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {stage === "sent" ? (
        <Box data-sb={TestIds.fleet.success} sx={{ px: 2.5, pb: 2.5 }}>
          <Typography sx={{ fontSize: "0.9375rem", color: zinc[100] }}>
            Sent to {summarizeRecipients(sentTo)}.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <ButtonBase
              onClick={onClose}
              sx={{ ...buttonSx, bgcolor: zinc[700], color: "white", "&:hover": { bgcolor: zinc[600] } }}
            >
              Done
            </ButtonBase>
          </Box>
        </Box>
      ) : (
        <Box sx={{ px: 2.5, pb: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography data-sb={TestIds.dispatch.fleetRecipients} sx={{ fontSize: "0.875rem", color: zinc[300] }}>
            To the {recipients.length === 1 ? "driver" : "drivers"} {scopeWord} on the dashboard:{" "}
            <Box component="span" sx={{ color: "white", fontWeight: 600 }}>
              {summary}
            </Box>
            . Change the filters or the ticked rows there to change who gets it.
          </Typography>
          {scope === "shown" && filters.length > 0 && (
            <Box data-sb={TestIds.dispatch.fleetFilters} sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {filters.map((filter) => (
                <Box
                  key={filter}
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: zinc[800],
                    border: `1px solid ${zinc[700]}`,
                    color: zinc[300],
                    fontSize: "0.75rem",
                  }}
                >
                  {filter}
                </Box>
              ))}
            </Box>
          )}
          {attaching ? (
            <Box sx={{ color: zinc[200] }}>
              <TusHooks setUploadContent={({ contentId }) => setUploadContentId(contentId)} />
            </Box>
          ) : (
            <Box data-sb={TestIds.fleet.messageInput}>
              <TextMessageInput
                aria-label="Fleet message text"
                placeholder="Type the message every recipient hears"
                textInputValue={text}
                textInputHandler={setText}
                textValid={textValid}
                validateTextHandler={setTextValid}
                characterLimit={MESSAGE_LIMIT}
                minRows={3}
                maxRows={10}
                showHint={false}
                disabled={stage === "sending"}
              />
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <ButtonBase
              data-sb={TestIds.dispatch.fleetTemplates}
              onClick={(event) => {
                setTemplatesAnchor(event.currentTarget);
                ampli.openTemplatesFleetMessage();
              }}
              disabled={stage === "sending" || attaching || !workspaceId}
              sx={{
                gap: 0.5,
                fontSize: "0.8125rem",
                color: zinc[300],
                "&:hover": { color: "white" },
                "&.Mui-disabled": { color: zinc[600] },
              }}
            >
              <TemplateIcon width={16} height={16} />
              Templates
            </ButtonBase>
            <ButtonBase
              onClick={() => {
                setAttaching((value) => !value);
                setUploadContentId(null);
              }}
              disabled={stage === "sending"}
              sx={{ gap: 0.5, fontSize: "0.8125rem", color: zinc[300], "&:hover": { color: "white" } }}
            >
              <AttachFileIcon sx={{ fontSize: 16 }} />
              {attaching ? "Type a message instead" : "Send a file instead"}
            </ButtonBase>
            <Box
              component="label"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.8125rem" }}
            >
              <Checkbox
                data-sb={TestIds.fleet.silent}
                size="small"
                checked={silent}
                onChange={(event) => setSilent(event.target.checked)}
                sx={{ p: 0.5, color: zinc[400] }}
              />
              Deliver silently
            </Box>
          </Box>
          <Popover
            open={templatesAnchor != null}
            anchorEl={templatesAnchor}
            onClose={() => setTemplatesAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{
              paper: {
                "data-sb": TestIds.dispatch.fleetTemplatesPopover,
                sx: toolPopoverPaperSx,
              } as React.ComponentProps<typeof Popover>["PaperProps"],
            }}
          >
            {/* The button stays disabled without a workspace; the check here only narrows the optional id. */}
            {templatesAnchor != null && workspaceId != null && (
              <TemplateChoices
                workspaceId={workspaceId}
                onChoose={(templateText) => {
                  // TextMessageInput validates only what is typed into it, so a template written in from here
                  // passes the same test itself: not blank, and within the limit.
                  setText(templateText);
                  setTextValid(templateText.trim().length > 0 && templateText.length <= MESSAGE_LIMIT);
                  setTemplatesAnchor(null);
                }}
              />
            )}
          </Popover>
          {error && <Typography sx={{ fontSize: "0.8125rem", color: "#f87171" }}>{error}</Typography>}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <ButtonBase
              onClick={onClose}
              disabled={stage === "sending"}
              sx={{ ...buttonSx, color: zinc[300], "&:hover": { color: "white" } }}
            >
              Cancel
            </ButtonBase>
            <ButtonBase
              data-sb={TestIds.fleet.send}
              onClick={() => void send()}
              disabled={!canSend}
              sx={{
                ...buttonSx,
                bgcolor: "var(--color-brand-primary-600)",
                color: "white",
                "&:hover": { bgcolor: "var(--color-brand-primary-500)" },
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {stage === "sending" ? "Sending…" : `Send to ${summary}`}
            </ButtonBase>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
